import { useEffect, useRef, useState } from 'react';
import type { Identity, LetterSignature, Subject } from '../types';
import { errorMessage } from '../lib/db';
import {
  DEFAULT_LETTER_THEME,
  LETTER_THEMES,
  getLetterTheme,
  themeVars,
} from '../lib/letterThemes';
import {
  DEFAULT_LETTER_FONT,
  getLetterFont,
  nextLetterFont,
} from '../lib/letterFonts';
import {
  DEFAULT_LETTER_SIZE,
  getLetterSize,
  sizeVars,
  stepLetterSize,
} from '../lib/letterSizes';
import { fetchUserDevices } from '../lib/relay';
import { sendToDevices } from '../lib/signal';
import { SignaturePad } from '../components/SignaturePad';
import { LetterSignatureView } from '../components/LetterSignatureView';
import { ScheduleControl } from '../components/ScheduleControl';

type Props = {
  me: Identity;
  subject: Subject;
  onSent: (args: {
    id: string;
    body: string;
    title?: string;
    sent_at: string;
    letter_theme?: string;
    letter_font?: string;
    letter_size?: string;
    letter_signature?: LetterSignature;
    scheduled_for?: string;
  }) => void;
  onClose: () => void;
};

const TITLE_MAX = 80;

export function LetterCompose({ me, subject, onSent, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [themeId, setThemeId] = useState(DEFAULT_LETTER_THEME.id);
  const [fontId, setFontId] = useState(DEFAULT_LETTER_FONT.id);
  const [sizeId, setSizeId] = useState(DEFAULT_LETTER_SIZE.id);
  const [signature, setSignature] = useState<LetterSignature | null>(null);
  const [signing, setSigning] = useState(false);
  const [deliverAt, setDeliverAt] = useState<Date | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const theme = getLetterTheme(themeId);
  const font = getLetterFont(fontId);
  const size = getLetterSize(sizeId);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Auto-grow the body textarea as you write — letters should feel
  // unbounded, not constrained to a small box.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(280, el.scrollHeight)}px`;
  }, [body]);

  const wordCount = body.trim() ? body.trim().split(/\s+/).length : 0;
  const readMins = Math.max(1, Math.round(wordCount / 220));

  const recipientLine =
    subject.kind === 'friend'
      ? `to @${subject.friend.username}`
      : `to ${subject.group.name} · ${subject.group.members.length + 1} people`;

  async function send() {
    const cleanBody = body.trim();
    if (!cleanBody) {
      setError('A letter needs a body.');
      return;
    }
    const cleanTitle = title.trim().slice(0, TITLE_MAX) || undefined;
    setBusy(true);
    setError(null);
    try {
      const sent_at = new Date().toISOString();
      const messageId = crypto.randomUUID();
      const letter_theme = themeId !== DEFAULT_LETTER_THEME.id ? themeId : undefined;
      const letter_font = fontId !== DEFAULT_LETTER_FONT.id ? fontId : undefined;
      const letter_size = sizeId !== DEFAULT_LETTER_SIZE.id ? sizeId : undefined;
      const letter_signature = signature ?? undefined;

      const basePayload = {
        sender_username: me.username,
        body: cleanBody,
        sent_at,
        message_id: messageId,
        kind: 'letter' as const,
        title: cleanTitle,
        letter_theme,
        letter_font,
        letter_size,
        letter_signature,
      };

      if (subject.kind === 'friend') {
        let devices = subject.friend.devices;
        if (!devices || devices.length === 0) {
          try {
            devices = await fetchUserDevices(subject.friend.id);
          } catch {
            // fall through to guard
          }
        }
        if (!devices || devices.length === 0) {
          throw new Error(
            `@${subject.friend.username} hasn't set up No Rush on a device yet, ` +
              'so they can\'t receive this letter. Ask them to open the app and ' +
              'verify their phone, then try again.',
          );
        }
        await sendToDevices({
          me,
          recipientDevices: devices,
          deliverAt,
          payload: basePayload,
        });
      } else {
        const group = subject.group;
        const allDevices = group.members.flatMap((m) => m.devices);
        if (allDevices.length === 0) {
          throw new Error(
            'No one in this group has set up No Rush on a device yet.',
          );
        }
        await sendToDevices({
          me,
          recipientDevices: allDevices,
          deliverAt,
          payload: {
            ...basePayload,
            group: { id: group.id, name: group.name },
          },
        });
      }

      onSent({
        id: messageId,
        body: cleanBody,
        title: cleanTitle,
        sent_at,
        letter_theme,
        letter_font,
        letter_size,
        letter_signature,
        scheduled_for: deliverAt ? deliverAt.toISOString() : undefined,
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="letter-compose-overlay" role="dialog" aria-modal="true">
      <div className="letter-compose" style={{ ...themeVars(theme), ...sizeVars(size) }}>
        <header className="letter-compose-head">
          <button
            type="button"
            className="btn btn-ghost btn-small"
            onClick={onClose}
            disabled={busy}
          >
            ← Back to chat
          </button>
          <span className="letter-compose-meta">
            from @{me.username} · {recipientLine}
          </span>
          <button
            type="button"
            className="btn btn-primary btn-small"
            onClick={send}
            disabled={busy || !body.trim()}
          >
            {busy ? 'Sealing…' : deliverAt ? 'Schedule' : 'Send letter'}
          </button>
        </header>

        {/* Controls in two rows: paper colors on top (so they're never
            crowded out by the type controls), font + size below. */}
        <div className="letter-controls">
          <div className="letter-theme-row" role="radiogroup" aria-label="Paper">
            {LETTER_THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={t.id === themeId}
                aria-label={t.name}
                title={t.name}
                className={`letter-swatch ${t.id === themeId ? 'on' : ''}`}
                style={{ background: t.paper }}
                onClick={() => setThemeId(t.id)}
                disabled={busy}
              />
            ))}
          </div>
          <div className="letter-type-row">
            <button
              type="button"
              className="letter-font-spinner"
              onClick={() => setFontId((id) => nextLetterFont(id).id)}
              disabled={busy}
              title="Tap to change the writing"
              aria-label={`Font: ${font.name}. Tap to change.`}
            >
              <span className="letter-font-aa" style={{ fontFamily: font.stack }}>
                Aa
              </span>
              <span className="letter-font-name">{font.name}</span>
              <span className="letter-font-turn" aria-hidden="true">↻</span>
            </button>
            <div className="letter-size-stepper" role="group" aria-label="Text size">
              <button
                type="button"
                className="letter-size-btn"
                onClick={() => setSizeId((id) => stepLetterSize(id, -1).id)}
                disabled={busy || sizeId === 's'}
                aria-label="Smaller text"
              >
                A−
              </button>
              <span className="letter-size-label">{size.label}</span>
              <button
                type="button"
                className="letter-size-btn"
                onClick={() => setSizeId((id) => stepLetterSize(id, 1).id)}
                disabled={busy || sizeId === 'xl'}
                aria-label="Bigger text"
              >
                A+
              </button>
            </div>
            <ScheduleControl value={deliverAt} onChange={setDeliverAt} disabled={busy} />
          </div>
        </div>

        <div className="letter-compose-page">
          <input
            ref={titleRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            maxLength={TITLE_MAX}
            className="letter-compose-title"
            style={{ fontFamily: font.stack }}
            disabled={busy}
          />

          <textarea
            ref={bodyRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`Dear ${
              subject.kind === 'friend' ? `@${subject.friend.username}` : 'everyone'
            },\n\n`}
            className="letter-compose-body"
            style={{ fontFamily: font.stack }}
            disabled={busy}
            rows={8}
          />

          {/* Signature sits bottom-right, like a real letter. */}
          <div className="letter-sign-zone">
            {signature ? (
              <div className="letter-signature-block">
                <LetterSignatureView signature={signature} />
                <button
                  type="button"
                  className="btn btn-ghost btn-small letter-sign-redo"
                  onClick={() => setSigning(true)}
                  disabled={busy}
                >
                  Re-sign
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="letter-sign-btn"
                onClick={() => setSigning(true)}
                disabled={busy}
              >
                ✍️ Sign your letter
              </button>
            )}
          </div>

          <div className="letter-compose-foot">
            <span>
              {wordCount} {wordCount === 1 ? 'word' : 'words'} · {readMins} min read
            </span>
            <span>
              Sealed end-to-end, like any message. Just longer, and the recipient
              will see it as a letter.
            </span>
          </div>

          {error && <p className="error">{error}</p>}
        </div>
      </div>

      {signing && (
        <SignaturePad
          inkColor={theme.ink}
          onCancel={() => setSigning(false)}
          onDone={(sig) => {
            setSignature(sig);
            setSigning(false);
          }}
        />
      )}
    </div>
  );
}
